import React from "react";

import Box from "../Box";
import SlotsBar from "../SlotsBar";
import { DriveSlotItem, DriveSlot as DriveSlotType } from "../../types/drive";
import BaseDashboardCard from "../BaseDashboardCard";
import dayjs, { Dayjs } from "dayjs";
import TimeScale from "../TimeScale";
import styled from "styled-components";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { CTag, CText } from "@carrefour/design-system-components-react";
import { useSearchParams } from "react-router-dom";
import { Choice } from "../../types/commercial";
import InfoBarSlot from "../InfoBarSlot";
import SLotTabs from "../SLotTabs";
import { eventTrack } from "../../utils/gtag";
import { useTranslation } from "react-i18next";
import { formatPercentage } from "../../utils/numberUtils";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Paris");

const Text = styled(CText)<{ color?: string }>`
  color: ${(props) => props.color};
  font-family: system-ui;
`;
const BoxCustom = styled(Box)<{ gap?: string; isBorded?: boolean }>`
  gap: ${(props) => props.gap};
  display: flex;
  border-bottom: ${(props) => (props?.isBorded ? " 1px solid #D9D9D9" : "")};
`;
const CTagCustum = styled(CTag)`
  font-family: system-ui;
  border-radius: 4px;
  width: 100%;
  font-size: large;
  font-weight: 700;
  size: 28px;
`;

type DriveSlotsCardProps = {
  title: string;
  driveTodaySlots: DriveSlotType;
  driveNextDaySlots: DriveSlotType;
  ladTodaySlots: DriveSlotType;
  ladNextDaySlots: DriveSlotType;
  isUnavailable: boolean;
  importedDateTime?: string;
  isLoading: boolean;
  messageError?: string;
  range: string;
  options: Choice[];
  openInformationModal: () => void;
};

const computeTotalSlots = (slots: Array<DriveSlotItem>) => {
  return slots.reduce(
    (total, slot) => {
      return {
        totalBooked: total.totalBooked + slot.bookedCount,
        totalCapacity: total.totalCapacity + slot.capacity,
      };
    },
    {
      totalBooked: 0,
      totalCapacity: 0,
    }
  );
};
const computeTauxFermeture = (slots: Array<DriveSlotItem>) => {
  let booked = 0;
  let maxAttemps = 0;
  slots?.forEach((element) => {
    if (
      (element?.status === "CLOSE" || element?.status === "FULL") &&
      element?.startDateTime.hour() < 15
    )
      booked++;
    if (element?.startDateTime.hour() < 15) maxAttemps++;
  });

  return (booked / maxAttemps) * 100;
};

const SlotsCard = ({
  title,
  driveTodaySlots,
  driveNextDaySlots,
  ladTodaySlots,
  ladNextDaySlots,
  importedDateTime,
  range,
  options,
  openInformationModal,
  ...rest
}: DriveSlotsCardProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const date = dayjs().tz("Europe/Paris");

  const tauxClosedDrive =
    date.hour() < 17
      ? computeTauxFermeture(driveTodaySlots?.slotsDetail)
      : computeTauxFermeture(driveNextDaySlots?.slotsDetail);

  const tauxClosedLad =
    date.hour() < 17
      ? computeTauxFermeture(ladTodaySlots?.slotsDetail)
      : computeTauxFermeture(ladNextDaySlots?.slotsDetail);

  const getDefaultValueParams = () => {
    searchParams.set(range, options?.[0]?.value as string);
    setSearchParams(searchParams);
    // ttracking changed
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: options?.[0]?.value,
    });
    return options?.[0]?.value;
  };

  const RangeParam = searchParams.get(range) ?? getDefaultValueParams();

  const mergedList = RangeParam === "TODAY" ?
    [...driveTodaySlots?.slotsDetail, ...ladTodaySlots?.slotsDetail]
    : [...driveNextDaySlots?.slotsDetail, ...ladNextDaySlots?.slotsDetail];

  // Utiliser un objet pour supprimer les doublons (basé sur startDateTime)
  const startTimeSlots = Object.values(
    mergedList.reduce((acc: Record<string, Dayjs>, slot) => {
      const key = slot.startDateTime;
      acc[key.toISOString()] = slot.startDateTime;
      return acc;
    }, {})
  ).sort((a, b) => a.valueOf() - b.valueOf());

  const maxEndDateTime = mergedList.reduce<Dayjs | undefined>((max, slot) => {
    const endDateTime = dayjs(slot.endDateTime);
    return !max || endDateTime.isAfter(max) ? endDateTime : max;
  }, undefined);

  const { totalBooked: driveTotalBooked, totalCapacity: driveTotalCapacity } =
    computeTotalSlots(
      RangeParam === "TODAY"
        ? driveTodaySlots?.slotsDetail
        : driveNextDaySlots?.slotsDetail
    );
  const { totalBooked: ladTotalBooked, totalCapacity: ladTotalCapacity } =
    computeTotalSlots(
      RangeParam === "TODAY"
        ? ladTodaySlots?.slotsDetail
        : ladNextDaySlots?.slotsDetail
    );

  const currentDriveSlot =
    RangeParam === "TODAY" ? driveTodaySlots : driveNextDaySlots;

  const currentLadSlot =
    RangeParam === "TODAY" ? ladTodaySlots : ladNextDaySlots;

  const isManualEdited =
    driveTotalCapacity !== currentDriveSlot?.maxCapacitySlot ||
    ladTotalCapacity !== currentLadSlot?.maxCapacitySlot;

  const {t} =useTranslation();

  return (
    <BaseDashboardCard
      title={title}
      openInformationModal={openInformationModal}
      size="small"
      hasModal={false}
      importedDateTime={importedDateTime}
      {...rest}
    >
      <BoxCustom py={3} gap="24px" display={"flex"} flexDirection={"column"}>
        <SLotTabs RangeParam={RangeParam} options={options} />
        <BoxCustom gap="8px">
          <BoxCustom
            display={"flex"}
            flexDirection={"column"}
            gap={"2px"}
            textAlign={"left"}
            borderRadius={1}
            border={"1px solid #E5E5E5"}
            py={1}
            px={2}
            width={"100%"}
          >
            <Box display={"flex"} alignItems={"center"} textAlign={"left"}>
              <Text size={"s"} bold>
                {t('ecommerce.drive')}
              </Text>
            </Box>
            <Box display={"flex"} alignItems={"center"} textAlign={"left"}>
              <Text size={"s"}>{t('ecommerce.taux_fermeture_J')}</Text>
            </Box>

            <BoxCustom display={"flex"} alignItems={"end"}>
              <Text size="l" tagStyle="h2">
                {!isNaN(tauxClosedDrive)
                  ? `${formatPercentage(tauxClosedDrive, "")}`
                  : "-"}
              </Text>
              <Text tagStyle="p">{"%"}</Text>
            </BoxCustom>
          </BoxCustom>
          <BoxCustom
            display={"flex"}
            flexDirection={"column"}
            gap={"2px"}
            textAlign={"left"}
            borderRadius={1}
            border={"1px solid #E5E5E5"}
            py={1}
            px={2}
            width={"100%"}
          >
            <Box display={"flex"} alignItems={"center"} textAlign={"left"}>
              <Text size={"s"} bold>
                {t('ecommerce.lad')}
              </Text>
            </Box>
            <Box display={"flex"} alignItems={"center"} textAlign={"left"}>
              <Text size={"s"}>{t('ecommerce.taux_fermeture_J')}</Text>
            </Box>

            <BoxCustom display={"flex"} alignItems={"end"}>
              <Text size="l" tagStyle="h2">
                {!isNaN(tauxClosedLad)
                  ? `${formatPercentage(tauxClosedLad, "")}`
                  : "-"}
              </Text>
              <Text tagStyle="p">{"%"}</Text>
            </BoxCustom>
          </BoxCustom>
        </BoxCustom>
        {isManualEdited && (
          <BoxCustom textAlign={"left"}>
            <CTagCustum
              size="l"
              status="info"
              icon="alert-octagon"
              data-testid="slot-tag"
            >
              <Text color="#173EB4" bold>
                {t('ecommerce.msg_nbr_cmd_max_modifie_manuellement')}
              </Text>
            </CTagCustum>
          </BoxCustom>
        )}
        <Box display="flex" justifyContent={"center"} width="100%" mt={[1, 2]}>
          <BoxCustom
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            width={["20%", "10%"]}
            gap="12px"
          >
            <Text style={{ height: "25px" }}></Text>
            <Text style={{ height: "25px" }}></Text>
            <TimeScale slots={startTimeSlots} />
          </BoxCustom>

          <BoxCustom
            height={"100%"}
            width={currentLadSlot?.slotsDetail?.length === 0 ? "100%" : "50%"}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="18px"
          >
            <Box>
              <Text bold>{t('ecommerce.drive')}</Text>
              <Text
                bold
                color={
                  driveTotalCapacity !== currentDriveSlot?.maxCapacitySlot
                    ? "#173EB4"
                    : "#696969"
                }
              >{`${driveTotalBooked}/${
                currentDriveSlot?.maxCapacitySlot
                  ? currentDriveSlot?.maxCapacitySlot
                  : driveTotalCapacity
              }`}</Text>
            </Box>
            <SlotsBar
              slots={currentDriveSlot?.slotsDetail}
              minStartDateTime={startTimeSlots[0]}
              maxEndDateTime={maxEndDateTime}
              data-testid="slot-tag-drive"
            />
          </BoxCustom>
          {currentLadSlot.slotsDetail?.length > 0 && (
            <BoxCustom
              // bg={"red"}
              height={"100%"}
              display="flex"
              width={"50%"}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              gap="18px"
            >
              <Box>
                <Text bold>{t('ecommerce.lad')}</Text>
                <Text
                  bold
                  color={
                    ladTotalCapacity !== currentLadSlot?.maxCapacitySlot
                      ? "#173EB4"
                      : "#696969"
                  }
                >{`${ladTotalBooked}/${
                  currentLadSlot?.maxCapacitySlot
                    ? currentLadSlot?.maxCapacitySlot
                    : ladTotalCapacity
                }`}</Text>
              </Box>
              <SlotsBar
                slots={currentLadSlot?.slotsDetail}
                minStartDateTime={startTimeSlots[0]}
                maxEndDateTime={maxEndDateTime}
                data-testid="slot-tag-lad"
              />
            </BoxCustom>
          )}
        </Box>
        <InfoBarSlot />
      </BoxCustom>
    </BaseDashboardCard>
  );
};
export default SlotsCard;
